import { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from "firebase/firestore";
import { toast } from "react-hot-toast";
import { Plus, Trash2 } from "lucide-react";

export default function AgentBankManager({ user }) {
  const [agent, setAgent] = useState(null);

  const [country, setCountry] = useState("");
  const [district, setDistrict] = useState("");
  const [binanceId, setBinanceId] = useState("");

  const [bank, setBank] = useState({
    bankName: "",
    accountName: "",
    accountNumber: "",
    branch: "",
  });

  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      const ref = doc(db, "agents", user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const a = { id: snap.id, ...snap.data() };
        setAgent(a);
        setCountry(a.country || "");
        setDistrict(a.district || "");
        setBinanceId(a.binancePayId || "");
      } else {
        // create skeleton
        await setDoc(ref, {
          status: "pending",
          banks: [],
          country: "",
          district: "",
          binancePayId: "",
          feeOutstandingUSD: 0,
          feePendingUSD: 0,
          cardFeePerCardUSD: 1,
          createdAt: serverTimestamp(),
        });
        const s2 = await getDoc(ref);
        setAgent({ id: s2.id, ...s2.data() });
      }
    })();
  }, [user?.uid]);

  const saveProfile = async () => {
    if (!user?.uid) return;
    try {
      await updateDoc(doc(db, "agents", user.uid), {
        country,
        district,
        binancePayId: binanceId,
        updatedAt: serverTimestamp(),
      });
      toast.success("Profile saved");
    } catch (e) {
      console.error(e);
      toast.error("Failed to save");
    }
  };

  const addBank = async () => {
    const clean = {
      bankName: bank.bankName.trim(),
      accountName: bank.accountName.trim(),
      accountNumber: bank.accountNumber.trim(),
      branch: bank.branch.trim(),
      _id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    };
    if (!clean.bankName || !clean.accountName || !clean.accountNumber) {
      return toast.error("Bank name, account name & number are required");
    }
    try {
      await updateDoc(doc(db, "agents", user.uid), {
        banks: arrayUnion(clean),
      });
      setAgent((a) => ({ ...a, banks: [...(a?.banks || []), clean] }));
      setBank({ bankName: "", accountName: "", accountNumber: "", branch: "" });
      toast.success("Bank added");
    } catch (e) {
      console.error(e);
      toast.error("Failed to add bank");
    }
  };

  const removeBank = async (b) => {
    try {
      await updateDoc(doc(db, "agents", user.uid), { banks: arrayRemove(b) });
      setAgent((a) => ({
        ...a,
        banks: (a?.banks || []).filter((x) => x._id !== b._id),
      }));
    } catch (e) {
      console.error(e);
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="max-w-3xl mx-auto mt-8 md:mt-16">
      <h1 className="text-2xl font-bold mb-4">Agent Payment Methods</h1>

      <div className="p-4 rounded-2xl bg-slate-800 space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-sm text-slate-300">Country</div>
            <input
              className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          </label>
          <label className="block">
            <div className="text-sm text-slate-300">District</div>
            <input
              className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
            />
          </label>
        </div>
        <label className="block">
          <div className="text-sm text-slate-300">
            Binance Pay ID (optional)
          </div>
          <input
            className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
            value={binanceId}
            onChange={(e) => setBinanceId(e.target.value)}
            placeholder="e.g. 123456789"
          />
        </label>
        <button
          onClick={saveProfile}
          className="px-4 py-2 rounded-xl bg-lime-400 text-black font-semibold"
        >
          Save Profile
        </button>
      </div>

      <h2 className="text-lg font-semibold mt-8 mb-2">Banks</h2>
      <div className="p-4 rounded-2xl bg-slate-800">
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-sm text-slate-300">Bank Name</div>
            <input
              className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={bank.bankName}
              onChange={(e) =>
                setBank((s) => ({ ...s, bankName: e.target.value }))
              }
            />
          </label>
          <label className="block">
            <div className="text-sm text-slate-300">Account Name</div>
            <input
              className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={bank.accountName}
              onChange={(e) =>
                setBank((s) => ({ ...s, accountName: e.target.value }))
              }
            />
          </label>
          <label className="block">
            <div className="text-sm text-slate-300">Account Number</div>
            <input
              className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={bank.accountNumber}
              onChange={(e) =>
                setBank((s) => ({ ...s, accountNumber: e.target.value }))
              }
            />
          </label>
          <label className="block">
            <div className="text-sm text-slate-300">Branch (optional)</div>
            <input
              className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={bank.branch}
              onChange={(e) =>
                setBank((s) => ({ ...s, branch: e.target.value }))
              }
            />
          </label>
        </div>
        <button
          onClick={addBank}
          className="mt-3 px-3 py-2 rounded bg-indigo-400 text-black font-semibold inline-flex items-center gap-2"
        >
          <Plus size={16} /> Add Bank
        </button>

        <div className="mt-4 space-y-2">
          {(agent?.banks || []).map((b) => (
            <div
              key={b._id}
              className="p-3 bg-slate-700 rounded flex items-center justify-between"
            >
              <div className="text-sm">
                <div className="font-semibold">{b.bankName}</div>
                <div>
                  {b.accountName} · {b.accountNumber}
                  {b.branch ? ` · ${b.branch}` : ""}
                </div>
              </div>
              <button
                onClick={() => removeBank(b)}
                className="p-2 rounded bg-slate-600"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {!(agent?.banks || []).length && (
            <div className="text-slate-400 text-sm">No banks yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
